import {QueryClient} from '@tanstack/react-query'

import {
  CASBService,
  AnalyticsService,
  CredsService,
  EndpointsService,
  MonolithService,
  PoliciesService,
  SettingsService,
  SIEMService,
} from 'services/api'
import {QUERY_LIMIT} from 'features/Endpoints/constant'
import {
  endpointsMetadataQueryFn,
  ENDPOINTS_METADATA_QUERY_KEY,
  GET_ENDPOINTS_QUERY_KEY,
} from 'features/Endpoints/useEndpointsQuery'
import {prefetchCacheTime} from './prefetch.config'
import {
  ActivityByTimeRequest,
  AnalyticsSearchPayloadType,
  DiagnosticPayloadType,
  EndpointAssociatedUserType,
  GetTopTenBlocksAction,
  PoliciesClashesPayloadInterface,
  ProductivityRequest,
  ProductivitySearchInputState,
  ReactQueryFetchFunctionMapType,
  WarmUpFunctionMap,
  CASBReviewFilesRequest,
  CASBProviderType,
} from 'interfaces'
import {shadowITTopAppsQueryFn} from 'features/Analytics/ShadowIT/RightViews/TopApplicationsView/useShadowITTopAppsQuery'
import {shadowITStreamChartQueryFn} from 'features/Analytics/ShadowIT/useShadowITStreamChartQuery'
import {getSearchSuggestions} from 'features/Analytics/Widgets/AnalyticsPredictiveSearch/service'
import {BLOCK_PAGES_QUERY_KEY} from 'features/Settings/BlockPages/useBlockPagesQuery'
import {
  SUBSCRIPTION_DETAILS_QUERY_KEY,
  subscriptionDetailsQueryFunction,
} from 'features/Settings/Billing/useSubscriptionDetailsQuery'
import {
  SIEM_HTTP_QUERY_KEY,
  SIEM_S3_QUERY_KEY,
} from 'features/Settings/SIEM/hooks'

/**
 * @description Prefetch analytics policies 7 day trend data on analytics overview mount
 * @param queryClient - react-query query client
 * @returns void
 */
const fetchAnalyticsPolicies7DayTrendData = async (
  queryClient: QueryClient,
) => {
  queryClient.prefetchQuery(
    ['get7DayTrend'],
    () => AnalyticsService.get7DayTrend(),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch analytics policies top-blocks data on analytics overview mount
 * @param queryClient - react-query query client
 * @returns void
 */
const fetchAnalyticsPoliciesTopBlocksData = async (
  queryClient: QueryClient,
  queryKey: string[],
  payload: GetTopTenBlocksAction,
) => {
  queryClient.prefetchQuery(
    queryKey,
    () => AnalyticsService.getTopTenBlocks(payload),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch policies data on app mount
 * @param queryClient - react-query query client
 * @returns void
 */
const fetchPoliciesData = async (queryClient: QueryClient) => {
  queryClient.prefetchInfiniteQuery(
    ['getPolicies'],
    () => PoliciesService.getPolicies({first: 100}),
    {
      getNextPageParam: lastPage => {
        const pageInfo = lastPage?.data?.data?.pageInfo
        return pageInfo?.hasNextPage ? pageInfo.endCursor : undefined
      },
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch CASB applications data on app mount
 * @param queryClient - react-query query client
 * @returns void
 */
const fetchCASBApplicationsData = async (queryClient: QueryClient) => {
  queryClient.prefetchQuery(['getCASBApps'], () => CASBService.getCASBApps(), {
    cacheTime: prefetchCacheTime,
  })
}

/**
 * @description Prefetch CASB overview data on app mount
 * @param queryClient - react-query query client
 * @returns void
 */
const fetchCASBOverviewData = async (
  queryClient: QueryClient,
  params: CASBProviderType,
) => {
  queryClient.prefetchQuery(
    ['getCASBOverview', params.provider],
    () => CASBService.getCASBOverview({provider: params.provider}),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch CASB files data on app mount
 * @param queryClient - react-query query client
 * @returns void
 */
const fetchCASBFilesData = async (
  queryClient: QueryClient,
  params: CASBProviderType,
) => {
  queryClient.prefetchInfiniteQuery(
    ['getCASBFiles'],
    () => CASBService.getCASBFiles({provider: params.provider}),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch CASB file details on navigating to CASB Overview page
 * @param queryClient - react-query query client
 * @param queryKey - queryKey for react-query call
 * @param payload - id, platform
 */
const fetchCASBFileDetails = async (
  queryClient: QueryClient,
  queryKey: string[],
  payload: CASBReviewFilesRequest,
) => {
  queryClient.prefetchQuery(
    queryKey,
    () => CASBService.getCASBReviewFileInfo(payload),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch endpoints data on app mount
 * @param queryClient - react-query query client
 * @returns void
 */
const fetchEndpointsData = async (queryClient: QueryClient) => {
  const filterOpts = {
    first: QUERY_LIMIT,
  }

  queryClient.prefetchInfiniteQuery(
    [GET_ENDPOINTS_QUERY_KEY, filterOpts],
    ({pageParam}) =>
      EndpointsService.getEndpointsSearch({
        ...filterOpts,
        after: pageParam,
      }),
    {
      getNextPageParam: lastPage => lastPage?.data?.data?.pageInfo?.endCursor,
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch endpoints metadata on app mount
 * @param queryClient - react-query query client
 * @returns void
 */
const fetchEndpointsMetaData = async (queryClient: QueryClient) => {
  queryClient.prefetchQuery(
    ENDPOINTS_METADATA_QUERY_KEY,
    endpointsMetadataQueryFn,
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch analytics productivity data on analytics overview mount
 * @param queryClient - react-query query client
 * @param queryKey - react-query query key
 * @param payload - params for API call
 * @returns void
 */
const fetchAnalyticsProductivityData = async (
  queryClient: QueryClient,
  queryKey: string[],
  payload: ProductivityRequest,
) => {
  queryClient.prefetchQuery(
    queryKey,
    () => AnalyticsService.getProductivity(payload),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}
/**
 * @description Prefetch settings general data on app mount
 * @param queryClient - react-query query client
 * @returns void
 */
const fetchSettingsGeneralData = async (queryClient: QueryClient) => {
  queryClient.prefetchQuery(['getAdminsList'], () =>
    SettingsService.getInvitedAdmin(),
  )
}

/**
 * @description Prefetch analytics productivity activity time data on analytics overview mount
 * @param queryClient - react-query query client
 * @param queryKey - react-query query key
 * @param payload - params for API call
 */
const fetchAnalyticsProductivityActivityTimeData = async (
  queryClient: QueryClient,
  queryKey: string[],
  payload: ActivityByTimeRequest,
) => {
  queryClient.prefetchQuery(
    queryKey,
    () => AnalyticsService.getActivityByTime(payload),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch analytics shadowIT top apps data on analytics overview mount
 * @param queryClient - react-query query client
 * @param queryKey - react-query query key
 * @param payload - params for API call
 */
const fetchAnalyticsShadowITTopAppsData = async (
  queryClient: QueryClient,
  queryKey: string[],
  payload: {
    searchType: string
    searchTerm: string
  },
) => {
  queryClient.prefetchQuery(
    queryKey,
    () => shadowITTopAppsQueryFn(payload?.searchType, payload?.searchTerm),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch analytics shadowIT stream charts data on analytics overview mount
 * @param queryClient - react-query query client
 * @param queryKey - react-query query key
 * @param payload - params for API call
 */
const fetchAnalyticsShadowITStreamChartsData = async (
  queryClient: QueryClient,
  queryKey: string[],
  payload: {
    shadowITSearchQuery: ProductivitySearchInputState
  },
) => {
  queryClient.prefetchQuery(
    queryKey,
    () => shadowITStreamChartQueryFn(payload.shadowITSearchQuery),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch settings API creds data on settings general mount
 * @param queryClient - react-query query client
 */
const fetchAPICredsData = async (queryClient: QueryClient) => {
  queryClient.prefetchQuery(
    ['api-keys-list'],
    () => CredsService.getAPIKeys(),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch settings block pages data on settings general mount
 * @param queryClient - react-query query client
 */
const fetchBlockPagesData = async (queryClient: QueryClient) => {
  queryClient.prefetchQuery(
    [BLOCK_PAGES_QUERY_KEY],
    () => SettingsService.getBlockPages(),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch settings SIEM S3 data on settings general mount
 * @param queryClient - react-query query client
 */
const fetchSiemS3Data = async (queryClient: QueryClient) => {
  queryClient.prefetchQuery(
    [SIEM_S3_QUERY_KEY],
    () => SIEMService.getSiemS3Integration(),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch settings SIEM HTTP data on settings general mount
 * @param queryClient - react-query query client
 */
const fetchSiemHttpData = async (queryClient: QueryClient) => {
  queryClient.prefetchQuery(
    [SIEM_HTTP_QUERY_KEY],
    () => SIEMService.getSiemHttpIntegration(),
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Prefetch settings billing data on settings general mount
 * @param queryClient - react-query query client
 */
const fetchBillingData = async (queryClient: QueryClient) => {
  queryClient.prefetchQuery(
    [SUBSCRIPTION_DETAILS_QUERY_KEY],
    subscriptionDetailsQueryFunction,
    {
      cacheTime: prefetchCacheTime,
    },
  )
}

/**
 * @description Warm up analytics search on analytics overview mount
 * @returns payload - searchTerm emailId of currently logged in user
 */
const warmAnalyticsSearch = async (payload: AnalyticsSearchPayloadType) => {
  await getSearchSuggestions(payload.searchTerm)
}

/**
 * @description Warm up endpoint diagnostics on mounting of endpoint details page
 * @param payload - agent
 * @returns void
 */
const warmEndpointDiagnostics = async (payload: DiagnosticPayloadType) => {
  await MonolithService.getDiagnosticResult(payload)
}

/**
 * @description Warm up associated users for select endpoint on mounting of endpoint details page
 * @param payload - agentUUID
 * @returns void
 */
const warmAssociatedUsers = async (payload: EndpointAssociatedUserType) => {
  await MonolithService.getAssociatedUsers(payload)
}

/**
 * @description Warm up policies clashes on navigating to policies page
 * @param payload - policyName, lastEvaluatedKey
 * @returns void
 */
const warmPolicyCategories = async (
  payload: PoliciesClashesPayloadInterface,
) => {
  await PoliciesService.getPolicyDetails(payload.policyName)
}

/**
 * @description Warm up policies block pages on navigating to policies page
 * @returns void
 */
const warmPolicyBlockPages = async () => {
  await SettingsService.getBlockPages()
}

/**
 * @description Warm up policies assignment on navigating to policies page
 * @param payload - policyName, lastEvaluatedKey
 * @returns void
 */
const warmPolicyAssignment = async (
  payload: PoliciesClashesPayloadInterface,
) => {
  await PoliciesService.getPolicyAssignee(payload.policyName)
}

/**
 * @description Warm up function for policies CAC app on navigating to policies page
 * @param payload - params for API call
 * @returns void
 */
const warmPolicyCACApp = async (payload: PoliciesClashesPayloadInterface) => {
  await PoliciesService.getCloudApp(payload)
}

/**
 * @description Warm up functions for policy bypass on navigating to policies page
 * @param payload - policyName
 */
const warmPolicyBypass = async (payload: PoliciesClashesPayloadInterface) => {
  await PoliciesService.getBypass(payload)
}

/**
 * @description React query functions for react-query APIs
 */
const reactQueryFetchFunctionMap: ReactQueryFetchFunctionMapType = {
  fetchPoliciesData,
  fetchCASBApplicationsData,
  fetchCASBOverviewData,
  fetchCASBFilesData,
  fetchEndpointsData,
  fetchEndpointsMetaData,
  fetchAnalyticsPolicies7DayTrendData,
  fetchAnalyticsPoliciesTopBlocksData,
  fetchAnalyticsProductivityData,
  fetchAnalyticsProductivityActivityTimeData,
  fetchAnalyticsShadowITTopAppsData,
  fetchAnalyticsShadowITStreamChartsData,
  fetchSettingsGeneralData,
  fetchAPICredsData,
  fetchBillingData,
  fetchBlockPagesData,
  fetchSiemS3Data,
  fetchCASBFileDetails,
  fetchSiemHttpData,
}

/**
 * @description Warm up functions for non-react-query endpoints
 */
const warmUpFunctionMap: WarmUpFunctionMap = {
  warmEndpointDiagnostics,
  warmAssociatedUsers,
  warmAnalyticsSearch,
  warmPolicyCategories,
  warmPolicyBlockPages,
  warmPolicyAssignment,
  warmPolicyCACApp,
  warmPolicyBypass,
}

export {reactQueryFetchFunctionMap, warmUpFunctionMap}
